// Cuestionario inline del chat — tool `preguntar` del asistente v2.
//
// Cuando al agente le falta información para generar (¿para qué red? ¿qué
// tono? ¿con o sin modelo?), en vez de escribirlo en prosa y esperar que el
// usuario conteste todo de memoria, muestra esta card: una o VARIAS preguntas,
// cada una con opciones para tocar. Cada pregunta es de opción única o
// múltiple según la marque el agente (ADR-2026-09-08-preguntas-con-opciones).
//
// Contrato con el agente (input): `{ intro?, preguntas: [{ id?, pregunta,
// opciones, multiple?, otra? }] }`. Devuelve
// `{ respuestas: [{ id, pregunta, elegidas: string[] }] }`.
//
// Las opciones se aceptan como string suelto o como `{ etiqueta, detalle }`:
// un modelo escribe cualquiera de las dos formas y romper la card por eso
// sería perder el turno entero.

import React, { useMemo, useState } from 'react';
import { Check, Pencil } from 'lucide-react';
import type { V2ToolCard } from '../useV2Chat';
import { palabraDeModeloActual } from '../../../lib/nombreDeModelo';

export interface PreguntaOpcion {
  etiqueta: string;
  detalle?: string;
}

export interface Pregunta {
  id: string;
  pregunta: string;
  opciones: PreguntaOpcion[];
  multiple: boolean;
  otra: boolean;
}

/** Respuesta que se le manda de vuelta al agente. */
export interface RespuestaPregunta {
  id: string;
  pregunta: string;
  elegidas: string[];
}

/** Marcador de la opción "Otra…": no puede chocar con una etiqueta real. */
const OTRA = '__otra__';

/** Normaliza lo que mandó el agente. Tolerante a propósito — ver cabecera. */
export const normalizarPreguntas = (input: any): Pregunta[] => {
  const crudas = Array.isArray(input?.preguntas)
    ? input.preguntas
    : // Una sola pregunta suelta en la raíz: el caso más probable de que el
      // modelo se saltee el array.
      input?.pregunta
      ? [input]
      : [];
  return crudas
    .map((p: any, i: number): Pregunta => {
      const opciones: PreguntaOpcion[] = (Array.isArray(p?.opciones) ? p.opciones : [])
        .map((o: any) =>
          typeof o === 'string'
            ? { etiqueta: o }
            : { etiqueta: String(o?.etiqueta ?? o?.label ?? ''), detalle: o?.detalle },
        )
        .filter((o: PreguntaOpcion) => o.etiqueta.trim().length > 0);
      return {
        id: String(p?.id ?? `p${i + 1}`),
        pregunta: String(p?.pregunta ?? p?.question ?? ''),
        opciones,
        multiple: !!(p?.multiple ?? p?.multi),
        // La escotilla de escribir a mano va por default: una lista cerrada
        // obliga a elegir algo que capaz no es lo que el usuario quiere.
        otra: p?.otra !== false,
      };
    })
    .filter((p: Pregunta) => p.pregunta.trim().length > 0 && p.opciones.length > 0);
};

export const PreguntasCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  onResolve: (respuestas: RespuestaPregunta[]) => void;
}> = ({ card, active, onResolve }) => {
  const intro: string | undefined = card.input?.intro;
  const preguntas = useMemo(() => normalizarPreguntas(card.input), [card.input]);

  // Selección por pregunta. En las de opción única el array tiene 0 o 1 item:
  // un solo tipo de estado para los dos modos, así el resto del componente no
  // se bifurca.
  const [elegidas, setElegidas] = useState<Record<string, string[]>>({});
  // Texto de la opción "Otra…", por pregunta.
  const [otroTexto, setOtroTexto] = useState<Record<string, string>>({});

  if (card.status === 'resolved' || card.status === 'cancelled') {
    const respuestas: RespuestaPregunta[] = card.response?.respuestas ?? [];
    return (
      <div className="p-4 flex flex-col gap-2.5">
        {respuestas.length === 0 ? (
          <p className="text-[13px] text-[#3C3C43]/55">Sin responder</p>
        ) : (
          respuestas.map((r) => (
            <div key={r.id}>
              <p className="text-[12.5px] text-[#3C3C43]/55 mb-1">{r.pregunta}</p>
              <div className="flex flex-wrap gap-1.5">
                {r.elegidas.map((e) => (
                  <span
                    key={e}
                    className="inline-block bg-[#F7F7F9] rounded-full px-3 py-1 text-[13px] font-medium text-brand-black/85"
                  >
                    {e}
                  </span>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  const toggle = (p: Pregunta, etiqueta: string) => {
    setElegidas((prev) => {
      const actual = prev[p.id] ?? [];
      if (!p.multiple) {
        // Opción única: volver a tocar la elegida la des-elige (si no, no hay
        // forma de corregir una respuesta en una card que todavía no mandaste).
        return { ...prev, [p.id]: actual[0] === etiqueta ? [] : [etiqueta] };
      }
      return {
        ...prev,
        [p.id]: actual.includes(etiqueta)
          ? actual.filter((e) => e !== etiqueta)
          : [...actual, etiqueta],
      };
    });
  };

  /** Lo elegido en una pregunta, con "Otra…" ya resuelto a su texto. */
  const respuestaDe = (p: Pregunta): string[] =>
    (elegidas[p.id] ?? [])
      .map((e) => (e === OTRA ? (otroTexto[p.id] ?? '').trim() : e))
      .filter((e) => e.length > 0);

  // Se contesta TODO o nada: media respuesta deja al agente adivinando justo
  // lo que vino a preguntar.
  const completo =
    preguntas.length > 0 && preguntas.every((p) => respuestaDe(p).length > 0);

  return (
    <div className="p-5">
      {intro && (
        <p className="text-[15px] font-semibold text-brand-black/90 mb-4">
          {intro}
        </p>
      )}

      <div className="flex flex-col gap-4">
        {preguntas.map((p, i) => {
          const sel = elegidas[p.id] ?? [];
          return (
            <div key={p.id}>
              <div className="flex items-baseline gap-2 mb-2">
                {/* El número sólo con más de una pregunta: en una sola es ruido. */}
                {preguntas.length > 1 && (
                  <span className="text-[12px] font-semibold text-brand-primary tabular-nums shrink-0">
                    {i + 1}
                  </span>
                )}
                <p className="text-[14.5px] font-medium text-brand-black/90">
                  {p.pregunta}
                </p>
                {p.multiple && (
                  <span className="text-[11.5px] text-[#3C3C43]/45 shrink-0 ml-auto">
                    varias
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {p.opciones.map((o) => {
                  const activa = sel.includes(o.etiqueta);
                  return (
                    <button
                      key={o.etiqueta}
                      type="button"
                      disabled={!active}
                      onClick={() => toggle(p, o.etiqueta)}
                      title={o.detalle}
                      aria-pressed={activa}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-[13.5px] font-medium transition-all disabled:opacity-50 ${
                        activa
                          ? 'bg-brand-primary border-brand-primary text-white'
                          : 'bg-white border-neutral-200 text-brand-black/80 hover:border-brand-primary/40'
                      }`}
                    >
                      {/* El tilde sólo en múltiple: en opción única el relleno
                          ya dice cuál está elegida, y el ícono corría el texto
                          de todas las pills al tocarlas. */}
                      {p.multiple && activa && (
                        <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={3} />
                      )}
                      {o.etiqueta}
                    </button>
                  );
                })}

                {p.otra && (
                  <button
                    type="button"
                    disabled={!active}
                    onClick={() => toggle(p, OTRA)}
                    aria-pressed={sel.includes(OTRA)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-dashed text-[13.5px] font-medium transition-all disabled:opacity-50 ${
                      sel.includes(OTRA)
                        ? 'bg-[#1644EB0F] border-brand-primary/50 text-brand-primary'
                        : 'bg-white border-neutral-300 text-[#3C3C43]/70 hover:border-brand-primary/40'
                    }`}
                  >
                    <Pencil className="w-3.5 h-3.5 shrink-0" />
                    Otra
                  </button>
                )}
              </div>

              {sel.includes(OTRA) && (
                <input
                  type="text"
                  autoFocus
                  disabled={!active}
                  value={otroTexto[p.id] ?? ''}
                  onChange={(e) =>
                    setOtroTexto((prev) => ({ ...prev, [p.id]: e.target.value }))
                  }
                  placeholder="Escribí tu respuesta…"
                  className="mt-2 w-full rounded-xl border border-neutral-200 px-3 py-2 text-[13.5px] text-brand-black/90 placeholder:text-[#3C3C43]/35 outline-none focus:border-brand-primary/50"
                />
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        disabled={!active || !completo}
        onClick={() =>
          onResolve(
            preguntas.map((p) => ({
              id: p.id,
              pregunta: p.pregunta,
              elegidas: respuestaDe(p),
            })),
          )
        }
        className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
      >
        {/* El texto no cambia con la validación: un botón que dice "Elegí una
            opción" y encima está deshabilitado explica dos veces lo mismo. */}
        Continuar
      </button>
    </div>
  );
};

/**
 * Cuestionario de MUESTRA para ver la card sin depender del agente: se monta
 * escribiendo `/preguntas` en el composer del asistente v2. Trae los tres
 * casos que la card tiene que resolver — opción única, opción múltiple y una
 * pregunta con más opciones — para poder mirarlos juntos.
 */
export const PREGUNTAS_DEMO = {
  intro: 'Para afinar la foto, contame tres cosas:',
  preguntas: [
    {
      id: 'red',
      pregunta: '¿Para qué red es?',
      opciones: ['Instagram', 'TikTok', 'Web', 'Mailing'],
    },
    {
      id: 'clima',
      pregunta: '¿Qué tiene que transmitir?',
      opciones: ['Cálido', 'Elegante', 'Divertido', 'Minimalista', 'Premium'],
      multiple: true,
    },
    {
      id: 'modelo',
      get pregunta() {
        return `¿Va con ${palabraDeModeloActual().singular}?`;
      },
      get opciones() {
        return [`Sí, con ${palabraDeModeloActual().singular}`, 'Solo el producto', 'Me da igual'];
      },
    },
  ],
};
